import React, { useEffect, useRef, useState } from 'react';

import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { CommonModal, EyeButton } from '../../TableUIKit';
import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';
import Modal from '../common/Modal';

const WaNotificationSentPage = () => {

  const waApi = useWaApi();

  // Personal data (mobile number, request/response JSON, attachment link) arrives masked from the service. Only
  // SYSTEM_ADMIN / ADMIN get the eye icon and the attachment download; USER never does. The service enforces the same
  // rule on every call, so hiding the controls is a convenience, not the access check.
  const { canViewPii } = useUserProfileAccess();
  // Values unmasked inline (mobile number, attachment link), by `${message id}:${field}`. Kept only until the list
  // changes, so an unmasked value never outlives the rows on screen.
  const [revealed, setRevealed] = useState({});
  const [revealing, setRevealing] = useState({});


  const [waNotifications, setWaNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // State for filters
  const initialFilters = {
    id: null,
    clientRequestId: null,
    clientGroupRequestId: null,
    notificationId: null,
    templateCategory: null,
    waTemplateName: null,
    waMessageStatus: null,
    status: null,
    repick: null,
    waMessageType: null,
    toWAUserPhoneNumber: null,
    senderPhoneNumberId: null,
    waMessageResponseId: null,
    createdDate: null,
    sentAt: null,
    futureDate: null
  };
  const [filters, setFilters] = useState(initialFilters);
  const resetFilters = () => {
    clearMessages();
    setFilters(initialFilters);
  };

  // State for filter options
  const [waTemplateCategories, setWaTemplateCategories] = useState([]);
  //const [waTemplateNames, setWaTemplateNames] = useState([]);

  //for handling pagination
  const [page, setPage] = useState(0); // Starting from the first page
  const [size, setSize] = useState(3); // Number of items per page
  const [totalPages, setTotalPages] = useState(0); // To store total pages

  // The row being edited in the Edit popup (null = popup closed), and the two values the service lets us change.
  const [editingNotification, setEditingNotification] = useState(null);
  const [editFutureDate, setEditFutureDate] = useState('');
  const [editRePick, setEditRePick] = useState('');
  const [editError, setEditError] = useState(null); // shown inside the popup, which stays open so the user can retry
  const [savingEdit, setSavingEdit] = useState(false);
  // After a save the response banner is scrolled into view (it sits above the table, which may be scrolled out of sight).
  const messageBannerRef = useRef(null);
  const [savedTick, setSavedTick] = useState(0);

  const [activeDetails, setActiveDetails] = useState(null);
  const [actionErrors, setActionErrors] = useState({});
  const [createError, setCreateError] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);
  const WA_MESSAGE_STATUSES = new Set([
    "DELIVERED", "READ",  "SENT", "FAILED"
  ]);
  const WA_MESSAGE_TYPES = new Set([
    "TEMPLATE", "DOCUMENT", "TEXT", "IMAGE", "LOCATION", "AUDIO", "VIDEO", "CONTACTS", "STICKER", "INTERACTIVE"
  ]);
  const resetEditForm = () => {
  setEditingNotification(null);
  setEditFutureDate('');
  setEditRePick('');
  setEditError(null);
  };

  const clearMessages = () => {
    setCreateError(null);
    setCreateMessage(null);
    setActionErrors({});
    resetEditForm();
  };

  // Whatever the list shows next (page, filter, edit, refresh) starts masked again.
  useEffect(() => {
    setRevealed({});
  }, [waNotifications]);

  useEffect(() => {
    if (savedTick > 0 && messageBannerRef.current && messageBannerRef.current.scrollIntoView) {
      messageBannerRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [savedTick]);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  useEffect(() => {

    const fetchFilterOptions = async () => {
      try {
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_CATEGORIES_ENDPOINT  
        );
        const { object } = response.data; // Assuming the response structure contains an array of template objects

        // Extracting necessary values for dropdowns
        const waTemplateCategories = [...new Set(object.map(waCategory => waCategory.templateCategory))]; // wa template category which is linked to template
    //    const waTemplateNames = [...new Set(object.map(waTemplate => waTemplate.waTemplateName))]; // template names

        // Set these unique values to state
        setWaTemplateCategories(waTemplateCategories);
     //   setWaTemplateNames(waTemplateNames);
      } catch (err) {
        setError(err.message || 'Error fetching filter options.');
      }
    };
    fetchFilterOptions(); // Call to fetch options
  }, [waApi]); // Runs once when the component mounts

  useEffect(() => {

    const fetchWaNotifications = async () => {
      try {
        const cleanedFilters = Object.fromEntries(
        Object.entries(filters).map(([key, value]) => [
          key,
          value === '' ? null : value,
        ])
      );
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_WA_NOTIFICATION_DETAILS_BY_PAGE_ENDPOINT,
          {
            params: {
              ...cleanedFilters,
              page,
              size,
            },
          }
        );


        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) {
          setWaNotifications(object.content); // Set the wanotification from the response object
          setTotalPages(object.totalPages); // Update total pages from response
        } else {
          setError(`Error: ${message}`); // Handle non-success status code
        }

        setLoading(false);
      } catch (err) {
        setError(err.message || 'An unexpected error occurred.');
        setLoading(false);
      }
    };

    fetchWaNotifications(); // Fetch whatsapp on component load
  }, [waApi, page, size]);



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };
      // Store the selected values in localStorage
      //sessionStorage.setItem(name, value);
      return newFilters;
    });
  };

  const handleDateChange = (date, fieldName) => {
    setFilters((prev) => ({
      ...prev,
      [fieldName]: date,
    }));
  };

  const handleEditClick = (notification) => {
    clearMessages();
    // Open the Edit popup for this row
    setEditingNotification(notification);
    setEditRePick(notification.rePick)//set rePick status to edit
    setEditFutureDate(
      notification.futureDate ? new Date(notification.futureDate) : null
    ); // Set the futureDate to edit
  };

  const closeEditPopup = () => {
    if (savingEdit) return; // a save is in flight: let it finish rather than orphan its result
    clearMessages();
  };

  const handleEditSubmit = async () => {
    if (!editingNotification) return;
    const notificationId = editingNotification.id;
    setEditError(null);
    setSavingEdit(true);
    try {
      // Update notification with PUT request
      const response = await waApi.put(
        `${process.env.REACT_APP_SET_WA_NOTIFICATION_DETAILS_ENDPOINT}/${notificationId}`,
        {
          rePick: editRePick === true,
          futureDate: editFutureDate,
        }
      );

      // Show the saved values in the grid straight away (the service stamps "Updated At" itself; this is the same moment, give or take)
      setWaNotifications((current) => current.map((notification) =>
        notification.id === notificationId
          ? { ...notification, rePick: editRePick === true, futureDate: editFutureDate, updatedTimestamp: new Date().toISOString() }
          : notification
      ));
      // Close the popup and show the service's answer above the grid
      resetEditForm();
      setCreateError(null);
      setCreateMessage(response.data.message);
      setSavedTick((tick) => tick + 1);
    } catch (err) {
      // Keep the popup open with the reason, so nothing the user entered is lost
      setEditError(err.response?.data?.message || err.message || 'An error occurred while updating the notification.');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleActivationStatus = async (notificationId, currentStatus) => {
    clearMessages();
    try {
      const newStatus = !currentStatus; // Toggle activation status
      const response = await waApi.patch(
        `${process.env.REACT_APP_SET_WA_NOTIFICATION_STATUS_ENDPOINT}/${notificationId}`,
        { status: newStatus }
      );
      // Update the local state after toggling the status
      setWaNotifications((prev) => prev.map(notification =>
        notification.id === notificationId ? { ...notification, status: newStatus } : notification
      ));
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [notificationId]: err.message || 'An error occurred while updating the status.' }));
    }
  };

  // The stored object key is a flat, prefixed name, but treat it as a path anyway so a key with
  // folders still shows a usable file name in the link.
  const fileNameOf = (objectKey) =>
    (objectKey || '').substring((objectKey || '').lastIndexOf('/') + 1) || objectKey;

  const revealKey = (notification, field) => `${notification.id}:${field}`;

  // One call to the service per unmasking. It checks the role, records who/where/what in the PII access log, and
  // only then returns the real value.
  const fetchRevealedValue = async (notification, field) => {
    const response = await waApi.post(
      `${process.env.REACT_APP_REVEAL_WA_NOTIFICATION_FIELD_ENDPOINT}/${notification.id}`,
      { field }
    );
    return response.data?.object?.value;
  };

  const revealErrorMessage = (err) =>
    err.response?.data?.message || err.message || 'An error occurred while fetching the value.';

  // Short values (mobile number, attachment link): shown in place of the mask; click again to mask them.
  const toggleInlineReveal = async (notification, field) => {
    setActionErrors({});
    const key = revealKey(notification, field);
    if (revealed[key] !== undefined) {
      setRevealed((prev) => {
        const { [key]: dropped, ...rest } = prev;
        return rest;
      });
      return;
    }
    setRevealing((prev) => ({ ...prev, [key]: true }));
    try {
      const value = await fetchRevealedValue(notification, field);
      setRevealed((prev) => ({ ...prev, [key]: value }));
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [notification.id]: revealErrorMessage(err) }));
    } finally {
      setRevealing((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Long JSON values: shown in the details dialog, which drops the value when it is closed.
  const revealIntoDialog = async (notification, field, title) => {
    setActionErrors({});
    const key = revealKey(notification, field);
    setRevealing((prev) => ({ ...prev, [key]: true }));
    try {
      const value = await fetchRevealedValue(notification, field);
      setActiveDetails({ text: value, title });
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [notification.id]: revealErrorMessage(err) }));
    } finally {
      setRevealing((prev) => ({ ...prev, [key]: false }));
    }
  };

  // A masked value plus, for roles that may unmask, the eye icon. `inline` swaps the value in the cell; otherwise
  // the real value opens in the details dialog.
  const maskedCell = (notification, field, label, { inline = false } = {}) => {
    const masked = notification[field];
    if (!masked) {
      return <td>-</td>;
    }
    const key = revealKey(notification, field);
    const shown = inline && revealed[key] !== undefined;
    return (
      <td>
        <span className={`pii-cell${shown ? ' pii-cell-open' : ''}`}>
        <span className="pii-masked">{shown ? revealed[key] : masked}</span>
        {canViewPii && (
          <EyeButton
            shown={shown}
            busy={revealing[key] === true}
            label={label}
            onClick={() => (inline ? toggleInlineReveal(notification, field) : revealIntoDialog(notification, field, label))}
          />
        )}
        </span>
      </td>
    );
  };

  const handleFileDownload = async (notification) => {
    clearMessages();
    try {
      // The service resolves the object key from the row itself and pulls the bytes out of
      // whichever cloud storage is configured - the key is never sent from here.
      const response = await waApi.get(
        `${process.env.REACT_APP_GET_WA_NOTIFICATION_FILE_ENDPOINT}/${notification.id}`,
        { responseType: 'blob' }
      );

      // Prefer the name the service sent; fall back to the key already on the row, since
      // Content-Disposition is not always exposed to JS through the gateway.
      const disposition = response.headers['content-disposition'];
      const match = disposition && disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      // The key on the row is masked, so only an unmasked one (eye icon) can name the file here.
      const unmaskedKey = revealed[revealKey(notification, 'fileStorageObjectKey')];
      const fileName = match ? decodeURIComponent(match[1]) : (unmaskedKey ? fileNameOf(unmaskedKey) : 'attachment');

      const blobUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // With responseType 'blob' even an error body arrives as a Blob, so read it back as text.
      let message = 'An error occurred while downloading the attachment.';
      if (err.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await err.response.data.text()).message || message;
        } catch (ignored) {
          // Not JSON - keep the default message.
        }
      } else if (err.message) {
        message = err.message;
      }
      setActionErrors((prev) => ({ ...prev, [notification.id]: message }));
    }
  };

  // Handle the status change to convert to boolean
  const handleStatusChange = (e) => {
   
  const { name, value } = e.target;
  const statusValue = (value === 'sent' || value === 'true') ? true : (value === 'not-sent' || value === 'false') ? false : null;
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: statusValue };
      return newFilters;
    });
  };

  // New function to apply filters
  const applyFilters = async (pageParam) => {

    setLoading(true); // Show loading while fetching filtered notifications
    clearMessages();
    var hitApplyFilter = null;
    if(page === 0) {
      hitApplyFilter = true;
    } else {
      hitApplyFilter = false;
    }
    if(pageParam===0) {
      setPage(0);
    }
    if(hitApplyFilter) {
      try {
        const cleanedFilters = Object.fromEntries(
          Object.entries(filters).map(([key, value]) => [
            key,
            value === '' ? null : value,
          ])
        );
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_WA_NOTIFICATION_DETAILS_BY_PAGE_ENDPOINT,
          {
            params: {
              ...cleanedFilters,
              page,
              size,
            },
          }
        );

        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) {
          setWaNotifications(object.content); // Set the whatsappnotification from the response object
          setTotalPages(object.totalPages); // Update total pages from response
        } else {
          setCreateError(message); // Handle non-success status code
        }

      } catch (err) {
        setCreateError(err.message || 'Failed to fetch notifications.');
      } finally {
        setLoading(false); // Reset loading state
      }
    }
  };

  const handleNextPage = () => {
    clearMessages();
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1); // Increment page number
    }
  };

  const handlePreviousPage = () => {
    clearMessages();
    if (page > 0) {
      setPage((prevPage) => prevPage - 1); // Decrement page number
    }
  };

  const handleSizeChange = (e) => {
    clearMessages();
    setSize(e.target.value);
    setPage(0); // Reset to first page on changing size
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error fetching App message notifications: {error}</div>;

  return (
    <div className="page-container">
      <h1>App Messages Notifications</h1>
      <div className="flex-row filter-section">
        <div className='filter-item'>
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} className="w-auto">
            <option value={3}>default</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>
      </div>
      <h3>Filters</h3>
      {/* Filter Form */}
      <div className="flex-row filter-section">
           <div className="filter-item">
          <label>Message Primary ID:</label>
          <input
            type="text"
            name="id"
            value={filters.id ?? ''}
            onChange={handleInputChange}
            placeholder="Only numeric value"
          />
        </div>
        <div className="filter-item">
          <label>WA Template Category:</label>
          <select name="templateCategory" onChange={handleInputChange} value={filters.templateCategory}>
            <option value="">All</option>
            {waTemplateCategories?.map((waTemplateCategory, index) => (
              <option key={index} value={waTemplateCategory}>
                {waTemplateCategory}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-item">
            <label>WA Message Status:</label>
            <select name="waMessageStatus" onChange={handleInputChange} value={filters.waMessageStatus ?? ''}>
              <option value="">All</option>
              {[...WA_MESSAGE_STATUSES]?.map((waStatus, index) => (
                <option key={index} value={waStatus}>
                  {waStatus}
                </option>
              ))}
           </select>
        </div>

        <div className="filter-item">
          <label>WA Template Name:</label>
           <input
            type="text"
            name="waTemplateName"
            value={filters.waTemplateName ?? ''}
            onChange={handleInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Message Type:</label>
          <select name="waMessageType" onChange={handleInputChange} value={filters.waMessageType ?? ''}>
            <option value="">All</option>
            {[...WA_MESSAGE_TYPES]?.map((messageType, index) => (
              <option key={index} value={messageType}>
                {messageType}
              </option>
            ))}
          </select>
      </div>
       <div className="filter-item">
          <label>To Phone Number:</label>
           <input
            type="text"
            name="toWAUserPhoneNumber"
            value={filters.toWAUserPhoneNumber ?? ''}
            onChange={handleInputChange}
            placeholder="Only numeric value"
          />
        </div>
        <div className="filter-item">
          <label>Sender Phone Number ID:</label>
           <input
            type="text"
            name="senderPhoneNumberId"
            value={filters.senderPhoneNumberId ?? ''}
            onChange={handleInputChange}
            placeholder="Only numeric value"
          />
        </div>
        <div className="filter-item">
          <label>WA Message Response ID:</label>
           <input
            type="text"
            name="waMessageResponseId"
            value={filters.waMessageResponseId ?? ''}
            onChange={handleInputChange}
          />
        </div>
        <div className="filter-item">
          <label>Client Request Id:</label>
          <input
            type="text"
            name="clientRequestId"
            value={filters.clientRequestId ?? ''}
            onChange={handleInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Group Client Request Id:</label>
          <input
            type="text"
            name="clientGroupRequestId"
            value={filters.clientGroupRequestId ?? ''}
            onChange={handleInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Notification Id:</label>
          <input
            type="text"
            name="notificationId"
            value={filters.notificationId ?? ''}
            onChange={handleInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Status:</label>
          <select name="status" onChange={handleStatusChange} value={filters.status === null ? '' : filters.status ? 'sent' : 'not-sent'}>
            <option value="">All</option>
            <option value="sent">Sent</option>
            <option value="not-sent">Not Sent</option>
          </select>
        </div>

        <div className="filter-item">
          <label>Repick:</label>
          <select name="repick" onChange={handleStatusChange} value={filters.repick === null ? '' : filters.repick ? 'true' : 'false'}>
            <option value="">All</option>
            <option value="true">TRUE</option>
            <option value="false">FALSE</option>
          </select>
        </div>

        <div className="filter-item">
          <label>Created At:</label>
          <DatePicker
            selected={filters.createdDate}
            onChange={(date) => handleDateChange(date, 'createdDate')}
            dateFormat="yyyy-MM-dd" // Adjust format as needed
            isClearable
            placeholderText="Select a date"
            customInput={
              <input
                readOnly
                onKeyDown={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
              />}
            showPopperArrow={false}
          />
        </div>

        <div className="filter-item">
          <label>Sent At:</label>
          <DatePicker
            selected={filters.sentAt}
            onChange={(date) => handleDateChange(date, 'sentAt')}
            dateFormat="yyyy-MM-dd" // Adjust format as needed
            isClearable
            placeholderText="Select a date"
            customInput={
              <input
                readOnly
                onKeyDown={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
              />}
            showPopperArrow={false}
          />
        </div>

        <div className="filter-item">
          <label>Future Date:</label>
          <DatePicker
            selected={filters.futureDate}
            onChange={(date) => handleDateChange(date, 'futureDate')}
            dateFormat="yyyy-MM-dd" // Adjust format as needed
            isClearable
            placeholderText="Select a date"
            customInput={
              <input
                readOnly
                onKeyDown={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
              />}
            showPopperArrow={false}
          />
        </div>
        

        <button type="button" onClick={() => applyFilters(0)}>Apply Filters</button>
        <button type="button" onClick={resetFilters}> Reset Filters</button>

      </div>

      {createMessage && (
        <div ref={messageBannerRef} style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createMessage}
        </div>
      )}

      {createError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createError}
        </div>
      )}

      <h3>App Notifications Status</h3>

      <div className="table-responsive">
        <table className="notifications-table">
          <thead>
            <tr>
              <th>Id</th>
              <th>WA message Type</th>
              <th>To WA User Phone</th>
              <th>Sender Phone Number Id</th>
              <th>WA Template Category</th>
              <th>WA Template Name</th>
              <th>Sent At</th>
              <th>Internal Status</th>
              <th>WA Message Satus</th>
              <th>Re-Pick</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Client Request ID</th>
              <th>Group Request ID</th>
              <th>Notification ID</th>
              <th>Future Date</th>
              <th>WA Message Response ID</th>
              <th>WA Request</th>
              <th>WA External Request</th>
              <th>WA Response</th>
              <th>Attachment</th>
              <th>Request Failure Reason</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {waNotifications.map((notification) => (
              <tr key={notification.id}>
                <td>{notification.id}</td>
                <td>{notification.waMessageType}</td>
                {maskedCell(notification, 'toWAUserPhoneNumber', 'To WA User Phone', { inline: true })}
                <td>{notification.senderPhoneNumberId}</td>
                <td>{notification.waTemplateCategory?.templateCategory || "N/A"}</td>
                <td>{notification.waTemplateName}</td>
                <td>{notification.sentAt ? new Date(notification.sentAt).toLocaleString() : 'N/A'}</td>
                <td>{notification.status ? 'Sent' : 'Not Sent'}</td>
                <td>{notification.webhookMessage?.messageStatus || "N/A"}</td>
                <td>
                  {notification.rePick == null ? "N/A" : notification.rePick ? "True" : "False"}
                </td>
                <td>{new Date(notification.createdTimestamp).toLocaleString()}</td>
                <td>{new Date(notification.updatedTimestamp).toLocaleString()}</td>
                <td>{notification.clientRequestId}</td>
                <td>{notification.clientGroupRequestId}</td>
                <td>{notification.notificationId}</td>
                <td>
                  {notification.futureDate ? new Date(notification.futureDate).toLocaleString() : 'N/A'}
                </td>
                <td>{notification.waMessageResponseId}</td>
                {maskedCell(notification, 'waRequest', 'WA Request')}
                {maskedCell(notification, 'waExternalRequest', 'WA External Request')}
                {maskedCell(notification, 'waResponse', 'WA Response')}
                <td>
                  {notification.fileStorageObjectKey ? (
                    <span className={`pii-cell${revealed[revealKey(notification, 'fileStorageObjectKey')] !== undefined ? ' pii-cell-open' : ''}`}>
                      {canViewPii ? (
                        // SYSTEM_ADMIN / ADMIN: the link downloads (recorded in the PII access log).
                        <button
                          type="button"
                          title="Download the attachment (this download is recorded)"
                          onClick={() => handleFileDownload(notification)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "#2563eb",
                            textDecoration: "underline",
                            cursor: "pointer",
                            font: "inherit",
                            wordBreak: "break-all",
                            textAlign: "left"
                          }}
                        >
                          {revealed[revealKey(notification, 'fileStorageObjectKey')] !== undefined
                            ? fileNameOf(revealed[revealKey(notification, 'fileStorageObjectKey')])
                            : notification.fileStorageObjectKey}
                        </button>
                      ) : (
                        // USER: the masked link only - not clickable, nothing to download.
                        <span className="pii-masked">{notification.fileStorageObjectKey}</span>
                      )}
                      {canViewPii && (
                        <EyeButton
                          shown={revealed[revealKey(notification, 'fileStorageObjectKey')] !== undefined}
                          busy={revealing[revealKey(notification, 'fileStorageObjectKey')] === true}
                          label="Attachment link"
                          onClick={() => toggleInlineReveal(notification, 'fileStorageObjectKey')}
                        />
                      )}
                    </span>
                  ) : (
                    'N/A'
                  )}
                </td>
                 <td>{notification.failureReason}</td>
                <td>
                  <div className="action-buttons">
                    <button type="button" onClick={() => handleEditClick(notification)}>Edit</button>
                    <button type="button" onClick={() => toggleActivationStatus(notification.id, notification.status)}>
                      {notification.status ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                  {actionErrors[notification.id] && (
                    <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                      {actionErrors[notification.id]}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 === totalPages}>Next</button>
      </div>
      {/* ONE MODAL TO RULE THEM ALL */}
      <CommonModal
        isOpen={!!activeDetails}
        onClose={() => setActiveDetails(null)}
        /* Pull the dynamic title from state */
        title={activeDetails?.title || "Details"}
        copyValue={activeDetails?.text}
      >
        {activeDetails?.text}
      </CommonModal>

      {/* Edit popup: the same size as every other create / edit popup (see .wa-popup in Global.css). Only Re-Pick and Future
          Date can be changed - the service ignores everything else - so the rest of the message is shown read only, masked
          exactly as in the grid. */}
      <Modal isOpen={!!editingNotification} onClose={closeEditPopup}>
        {editingNotification && (
          <div className="page-container">
            {editError && (
              <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
                {editError}
              </div>
            )}
            <form
              className="standard-form"
              onSubmit={(e) => { e.preventDefault(); handleEditSubmit(); }}
            >
              <h3>Edit App Message Notification</h3>
              {[
                ['Message Primary ID', editingNotification.id],
                ['Notification ID', editingNotification.notificationId],
                ['WA Message Type', editingNotification.waMessageType],
                ['To WA User Phone', editingNotification.toWAUserPhoneNumber],
                ['Sender Phone Number Id', editingNotification.senderPhoneNumberId],
                ['WA Template Name', editingNotification.waTemplateName],
                ['Internal Status', editingNotification.status ? 'Sent' : 'Not Sent'],
                ['Sent At', editingNotification.sentAt ? new Date(editingNotification.sentAt).toLocaleString() : 'N/A'],
                ['Client Request ID', editingNotification.clientRequestId],
                ['Group Request ID', editingNotification.clientGroupRequestId],
              ].map(([label, value]) => (
                <div className="form-group" key={label}>
                  <label>{label}:</label>
                  <input type="text" value={value ?? ''} disabled readOnly style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }} />
                </div>
              ))}

              <div className="form-group">
                <label htmlFor="edit-repick">Re-Pick:</label>
                <select
                  id="edit-repick"
                  value={
                    editRePick === null || editRePick === ""
                      ? ""
                      : editRePick
                        ? "true"
                        : "false"
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                      setEditRePick("");
                    } else {
                      setEditRePick(val === "true");
                    }
                  }}
                >
                  <option value="">Select</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="edit-future-date">Future Date:</label>
                <DatePicker
                  id="edit-future-date"
                  selected={editFutureDate}
                  onChange={(date) => setEditFutureDate(date)}
                  dateFormat="yyyy-MM-dd"
                  isClearable
                  placeholderText="Select a date"
                  minDate={tomorrow}
                  customInput={
                    <input
                      readOnly
                      onKeyDown={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onDrop={(e) => e.preventDefault()}
                    />}
                  showPopperArrow={false}
                />
              </div>

              <div className="form-actions" style={{ gap: "12px" }}>
                <button type="submit" disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save'}</button>
                <button type="button" onClick={closeEditPopup} disabled={savingEdit}>Cancel</button>
              </div>
            </form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WaNotificationSentPage;
